import { Brain, ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@bigpp/ui/lib/utils";

// Collapsible reasoning block. Starts collapsed; the header shimmers with the
// latest one-liner while streaming, then flips to "Thought for Ns". The user
// can expand at any point.

export function Reasoning({
  text,
  streaming,
  elapsedLabel,
}: {
  text: string;
  streaming: boolean;
  // Optional pre-formatted label. When omitted we compute one from the
  // internal streaming-duration timer.
  elapsedLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (streaming) {
      if (startRef.current === null) startRef.current = Date.now();
    } else if (startRef.current !== null) {
      setDuration(Math.max(1, Math.ceil((Date.now() - startRef.current) / 1000)));
      startRef.current = null;
    }
  }, [streaming]);

  const label = streaming
    ? teaseLine(text) || "Thinking…"
    : (elapsedLabel ?? (duration !== null ? `Thought for ${duration}s` : "Reasoning"));

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex items-center gap-1.5 text-[11px] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors w-full text-left"
      >
        <Brain className={cn("size-3 shrink-0", streaming && "animate-pulse")} />
        <span className={cn("truncate italic min-w-0", streaming && "shimmer-text")}>
          {label}
        </span>
        {open ? (
          <ChevronDown className="size-3 shrink-0 opacity-60" />
        ) : (
          <ChevronRight className="size-3 shrink-0 opacity-60" />
        )}
      </button>
      <div
        className={cn(
          "grid transition-all duration-200 ease-out",
          open && text.trim().length > 0
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex gap-2 pl-1 pt-1">
            <div className="w-0.5 shrink-0 rounded-full bg-[var(--color-border)]" />
            <div className="text-[11px] italic leading-snug whitespace-pre-wrap break-words text-[var(--color-muted-foreground)]">
              {text}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const teaseLine = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const lastPara = trimmed.split(/\n{2,}/).pop() ?? trimmed;
  const oneLine = lastPara.replace(/\s+/g, " ").trim();
  const limit = 90;
  return oneLine.length > limit ? `${oneLine.slice(0, limit - 1)}…` : oneLine;
};
